import { Alert, Button, Descriptions, Modal, Space, Spin, Typography } from 'antd';

import useLocale from '../../../hooks/useLocale';
import type { ClientService } from '../../../shared/api/ClientService';
import CommandActionButton from './CommandActionButton';
import CommandStatusTag from './CommandStatusTag';
import { CopyableId, DetailGroup } from './DetailParts';
import InfoLabel from './InfoLabel';
import LoadErrorAlert from './LoadErrorAlert';
import { DETAIL_MODAL_WIDTH, EMPTY_VALUE } from './constants';
import type { CommandAction } from './types';
import { useIntegrationCommand } from './useIntegrationMonitoring';
import { formatDateTime } from './utils';

interface IProps {
  commandId?: string;
  open: boolean;
  onClose: () => void;
  onAction: (action: CommandAction, commands: ClientService.IntegrationCommandDto[]) => void;
}

interface ITextProps {
  value?: string;
}

const LongText = ({ value }: ITextProps): JSX.Element => (
  <span className="integration-monitoring__long-text">{value || EMPTY_VALUE}</span>
);

const CommandDetailModal = ({ commandId, open, onClose, onAction }: IProps): JSX.Element => {
  const { t } = useLocale();
  const commandQuery = useIntegrationCommand(commandId, open);
  const command = commandQuery.data;

  return (
    <Modal
      centered
      destroyOnClose
      open={open}
      title={t.INTEGRATION_DETAILS_TITLE}
      width={DETAIL_MODAL_WIDTH}
      className="integration-monitoring__detail-modal"
      onCancel={onClose}
      footer={
        <Space>
          {command && (
            <>
              <CommandActionButton
                action="retry"
                statuses={[command.status]}
                label={t.INTEGRATION_RETRY_COMMAND}
                onClick={() => onAction('retry', [command])}
              />
              <CommandActionButton
                action="cancel"
                statuses={[command.status]}
                label={t.INTEGRATION_CANCEL_COMMAND}
                onClick={() => onAction('cancel', [command])}
              />
            </>
          )}
          <Button onClick={onClose}>{t.CLOSE}</Button>
        </Space>
      }
    >
      {commandQuery.isError && <LoadErrorAlert error={commandQuery.error} onRetry={() => commandQuery.refetch()} />}

      <Spin spinning={commandQuery.isFetching}>
        {command && (
          <>
            <DetailGroup title={t.INTEGRATION_COMMAND}>
              <Descriptions.Item label={t.INTEGRATION_COMMAND_ID}>
                <CopyableId value={command.id} />
              </Descriptions.Item>
              <Descriptions.Item label={t.INTEGRATION_FILE_NAME}>{command.fileName || EMPTY_VALUE}</Descriptions.Item>
              <Descriptions.Item label={t.INTEGRATION_FILE_ID}>
                <CopyableId value={command.fileId} />
              </Descriptions.Item>
              <Descriptions.Item label={t.INTEGRATION_COMMAND_TYPE}>
                {command.commandType || EMPTY_VALUE}
              </Descriptions.Item>
              <Descriptions.Item label={t.INTEGRATION_EVENT_TYPE}>{command.eventType || EMPTY_VALUE}</Descriptions.Item>
              <Descriptions.Item label={t.STATUS}>
                <CommandStatusTag status={command.status} isHolding={command.isHolding} />
                {command.isHolding && (
                  <Typography.Paragraph type="secondary" className="integration-monitoring__hint">
                    {t.INTEGRATION_HOLDING_HINT}
                  </Typography.Paragraph>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t.INTEGRATION_ATTEMPTS}>{command.attempts ?? EMPTY_VALUE}</Descriptions.Item>
            </DetailGroup>

            <DetailGroup title={t.INTEGRATION_GROUP_TIMING}>
              <Descriptions.Item label={t.CREATED}>{formatDateTime(command.creationTime)}</Descriptions.Item>
              <Descriptions.Item label={t.INTEGRATION_LAST_ATTEMPT}>
                {formatDateTime(command.lastAttemptTime)}
              </Descriptions.Item>
              <Descriptions.Item label={t.INTEGRATION_NEXT_ATTEMPT}>
                {formatDateTime(command.nextAttemptTime)}
              </Descriptions.Item>
              <Descriptions.Item label={t.INTEGRATION_COMPLETED}>
                {formatDateTime(command.completedTime)}
              </Descriptions.Item>
            </DetailGroup>

            <DetailGroup
              title={<InfoLabel label={t.INTEGRATION_LAST_FAILURE} hint={t.INTEGRATION_LAST_FAILURE_HINT} />}
            >
              <Descriptions.Item label={t.INTEGRATION_HTTP_STATUS_CODE}>
                {command.httpStatusCode ?? EMPTY_VALUE}
              </Descriptions.Item>
              <Descriptions.Item label={t.INTEGRATION_PROBLEM_TYPE}>
                <LongText value={command.problemType} />
              </Descriptions.Item>
              <Descriptions.Item label={t.INTEGRATION_ERROR_KEYS}>
                <LongText value={command.errorKeys} />
              </Descriptions.Item>
              <Descriptions.Item label={t.REASON}>
                <LongText value={command.failureReason} />
              </Descriptions.Item>
            </DetailGroup>

            <DetailGroup title={t.INTEGRATION_GROUP_CORRELATION}>
              <Descriptions.Item label={t.INTEGRATION_CORRELATION_ID}>
                <CopyableId value={command.correlationId} />
              </Descriptions.Item>
              <Descriptions.Item label={t.INTEGRATION_TRACE_ID}>
                <CopyableId value={command.traceId} />
              </Descriptions.Item>
              <Descriptions.Item label={t.INTEGRATION_EXTERNAL_ENTITY_ID}>
                <CopyableId value={command.externalEntityId} />
              </Descriptions.Item>
              <Descriptions.Item label={t.INTEGRATION_DEPENDS_ON}>
                <CopyableId value={command.dependsOnSyncId} />
              </Descriptions.Item>
            </DetailGroup>
          </>
        )}
      </Spin>

      <Alert type="info" showIcon message={t.INTEGRATION_PAYLOAD_NOTICE} />
    </Modal>
  );
};

export default CommandDetailModal;
